import React, { useCallback, useEffect, useState } from "react";
import moment from "moment";
import { useNavigate, useSearchParams } from "react-router-dom";
import "./FeedbackScreen.css";
import { getFeedbackOverview, getMyComplaints } from "../../api/feedback";
import SubmittedSurveys from "./SubmittedSurveys";
import { scoreForVisit, sortVisitsNewestFirst } from "./feedbackUtils";
import SurveyForm from "./SurveyForm";
import SurveyDone from "./SurveyDone";
import ComplaintForm from "./ComplaintForm";
import ComplaintsList from "./ComplaintsList";

/**
 * /feedback — patient feedback & complaints (NABH AAC.6.a).
 * Tabs: Surveys (pending invitations, rate a visit, confirmation with the
 * rating analysis) and Complaints (raise one → CMP-… reference; list with
 * status + history). Deep links: ?tab=complaints, ?appointmentId=<id>,
 * ?requestId=<id>. Spec: root PATIENT_FEEDBACK.md §8.
 */
const FeedbackScreen = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab") === "complaints" ? "complaints" : "surveys";
  const deepAppointmentId = searchParams.get("appointmentId") || "";
  const deepRequestId = searchParams.get("requestId") || "";

  const [tab, setTab] = useState(tabParam);

  // Surveys tab
  const [overview, setOverview] = useState(null);
  const [overviewError, setOverviewError] = useState("");
  const [loadingOverview, setLoadingOverview] = useState(true);
  const [surveyView, setSurveyView] = useState("list"); // list | form | done
  const [target, setTarget] = useState(null);
  const [result, setResult] = useState(null);
  const [notice, setNotice] = useState("");
  const [deepLinkHandled, setDeepLinkHandled] = useState(false);

  // Complaints tab
  const [complaints, setComplaints] = useState([]);
  const [complaintsError, setComplaintsError] = useState("");
  const [loadingComplaints, setLoadingComplaints] = useState(false);
  const [complaintsLoaded, setComplaintsLoaded] = useState(false);
  const [complaintView, setComplaintView] = useState("list"); // list | form | done
  const [complaintResult, setComplaintResult] = useState(null);

  const loadOverview = useCallback(async () => {
    setLoadingOverview(true);
    setOverviewError("");
    try {
      const data = await getFeedbackOverview();
      setOverview({
        pending: Array.isArray(data?.pending) ? data.pending : [],
        // Every invitation with its status (pending / completed / expired);
        // older backends only send `pending`.
        invitations: Array.isArray(data?.invitations)
          ? data.invitations
          : (Array.isArray(data?.pending) ? data.pending : []).map((p) => ({
              ...p,
              status: "pending",
            })),
        visits: sortVisitsNewestFirst(Array.isArray(data?.visits) ? data.visits : []),
        surveys: data?.surveys || {},
        responses: Array.isArray(data?.responses) ? data.responses : [],
      });
    } catch (err) {
      setOverviewError(err?.message || "Could not load your feedback");
    } finally {
      setLoadingOverview(false);
    }
  }, []);

  const loadComplaints = useCallback(async () => {
    setLoadingComplaints(true);
    setComplaintsError("");
    try {
      const data = await getMyComplaints();
      setComplaints(Array.isArray(data?.complaints) ? data.complaints : []);
      setComplaintsLoaded(true);
    } catch (err) {
      setComplaintsError(err?.message || "Could not load your complaints");
    } finally {
      setLoadingComplaints(false);
    }
  }, []);

  useEffect(() => {
    loadOverview();
  }, [loadOverview]);

  useEffect(() => {
    if (tab === "complaints" && !complaintsLoaded && !loadingComplaints) {
      loadComplaints();
    }
  }, [tab, complaintsLoaded, loadingComplaints, loadComplaints]);

  // Deep links: open the survey for ?appointmentId / ?requestId once the
  // overview is in.
  useEffect(() => {
    if (!overview || deepLinkHandled) return;
    if (!deepAppointmentId && !deepRequestId) return;
    setDeepLinkHandled(true);
    if (deepRequestId) {
      const inv = overview.pending.find(
        (p) => String(p.requestId) === deepRequestId
      );
      if (inv) return openInvitation(inv);
      setNotice("This survey invitation is no longer open.");
      return;
    }
    const visit = overview.visits.find(
      (v) => String(v.appointmentId) === deepAppointmentId
    );
    if (!visit) {
      setNotice("We could not find that visit in your completed visits.");
      return;
    }
    if (visit.rated) {
      setNotice("You have already rated this visit.");
      return;
    }
    openVisit(visit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overview, deepLinkHandled, deepAppointmentId, deepRequestId]);

  function switchTab(next) {
    setTab(next);
    setNotice("");
    const params = new URLSearchParams(searchParams);
    if (next === "complaints") params.set("tab", "complaints");
    else params.delete("tab");
    params.delete("appointmentId");
    params.delete("requestId");
    setSearchParams(params, { replace: true });
  }

  function openInvitation(inv) {
    setNotice("");
    setTarget({
      kind: "request",
      id: inv.requestId,
      clinicId: inv.clinicId,
      clinicName: inv.clinicName,
      doctorName: inv.doctorName,
      date: inv.sentAt,
    });
    setSurveyView("form");
  }

  function openVisit(visit) {
    setNotice("");
    setTarget({
      kind: "visit",
      id: visit.appointmentId,
      clinicId: visit.clinicId,
      clinicName: visit.clinicName,
      doctorName: visit.doctorName,
      date: visit.date,
    });
    setSurveyView("form");
  }

  function onSurveySubmitted(res) {
    setResult(res);
    setSurveyView("done");
    // The visit is now rated / the invitation is spent — refresh the lists.
    loadOverview();
  }

  function backToSurveys() {
    setSurveyView("list");
    setTarget(null);
    setResult(null);
  }

  function onComplaintCreated(res) {
    setComplaintResult(res);
    setComplaintView("done");
    setComplaintsLoaded(false);
  }

  function onClickBack() {
    if (window.history.length > 1) navigate(-1);
    else navigate("/", { replace: true });
  }

  const survey = target ? overview?.surveys?.[String(target.clinicId)] : null;

  return (
    <div className="fb_layout" id="layout">
      <div className="fb_header">
        <span>
          <svg
            onClick={onClickBack}
            className="fb_back"
            width="18"
            height="18"
            fill="currentColor"
            viewBox="0 0 16 16"
            role="button"
            aria-label="Back"
          >
            <path d="M15 8a.5.5 0 0 0-.5-.5H2.707l3.147-3.146a.5.5 0 1 0-.708-.708l-4 4a.5.5 0 0 0 0 .708l4 4a.5.5 0 0 0 .708-.708L2.707 8.5H14.5A.5.5 0 0 0 15 8z" />
          </svg>
        </span>
        <span className="fb_title">Feedback &amp; complaints</span>
        <div className="fb_tabs" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={tab === "surveys"}
            className={`fb_tab${tab === "surveys" ? " fb_tab_active" : ""}`}
            onClick={() => switchTab("surveys")}
          >
            Surveys
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === "complaints"}
            className={`fb_tab${tab === "complaints" ? " fb_tab_active" : ""}`}
            onClick={() => switchTab("complaints")}
          >
            Complaints
          </button>
        </div>
      </div>

      {tab === "surveys" ? (
        <div className="fb_body">
          {surveyView === "form" && target ? (
            <SurveyForm
              target={target}
              survey={survey}
              onSubmitted={onSurveySubmitted}
              onCancel={backToSurveys}
            />
          ) : surveyView === "done" ? (
            <SurveyDone result={result} onDone={backToSurveys} />
          ) : (
            <>
              {notice ? (
                <p className="fb_notice" role="status">
                  {notice}
                </p>
              ) : null}
              {loadingOverview && !overview ? (
                <p className="fb_muted fb_center">Loading…</p>
              ) : overviewError ? (
                <div className="fb_card">
                  <p className="fb_error" role="alert">
                    {overviewError}
                  </p>
                  <button type="button" className="fb_btn_secondary" onClick={loadOverview}>
                    Try again
                  </button>
                </div>
              ) : (
                <>
                  <h3 className="fb_section">Survey invitations</h3>
                  {overview.invitations.length === 0 ? (
                    <p className="fb_muted fb_center">No survey invitations yet.</p>
                  ) : (
                    <ul className="fb_list" aria-label="Survey invitations">
                      {overview.invitations.map((inv) => (
                        <li key={inv.requestId} className="fb_card fb_row">
                          <div className="fb_row_main">
                            <div className="fb_row_title">{inv.clinicName}</div>
                            <div className="fb_muted">{inv.doctorName}</div>
                            <div className="fb_muted fb_small">
                              Sent {moment(inv.sentAt).format("DD MMM YYYY")}
                              {inv.status === "completed" && inv.respondedAt
                                ? ` · completed ${moment(inv.respondedAt).format("DD MMM YYYY")}`
                                : inv.expiresAt
                                  ? ` · expires ${moment(inv.expiresAt).format("DD MMM YYYY")}`
                                  : ""}
                            </div>
                          </div>
                          {inv.status === "completed" ? (
                            <span className="fb_score" aria-label="Completed">
                              ✓ Completed
                              {typeof inv.overall === "number"
                                ? ` · ${inv.overall.toFixed(1)}/5`
                                : ""}
                            </span>
                          ) : inv.status === "expired" ? (
                            <span className="fb_chip fb_chip_muted">Expired</span>
                          ) : (
                            <button
                              type="button"
                              className="fb_btn_primary fb_btn_small"
                              onClick={() => openInvitation(inv)}
                            >
                              Take survey
                            </button>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}

                  <h3 className="fb_section">Your visits</h3>
                  {overview.visits.length === 0 ? (
                    <p className="fb_muted fb_center">
                      Completed visits will appear here so you can rate them.
                    </p>
                  ) : (
                    <ul className="fb_list" aria-label="Your visits">
                      {overview.visits.map((v) => {
                        const score = scoreForVisit(overview.responses, v.appointmentId);
                        return (
                          <li key={v.appointmentId} className="fb_card fb_row">
                            <div className="fb_row_main">
                              <div className="fb_row_title">{v.clinicName}</div>
                              <div className="fb_muted">{v.doctorName}</div>
                              <div className="fb_muted fb_small">
                                {v.date ? moment(v.date).format("DD MMM YYYY") : ""}
                              </div>
                            </div>
                            {v.rated ? (
                              <span className="fb_score" aria-label="Your rating">
                                ★ {score != null ? score.toFixed(1) : "Rated"}
                                {score != null ? <span className="fb_small"> / 5</span> : null}
                              </span>
                            ) : (
                              <button
                                type="button"
                                className="fb_btn_primary fb_btn_small"
                                onClick={() => openVisit(v)}
                              >
                                Rate this visit
                              </button>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  )}

                  <SubmittedSurveys refreshKey={overview.responses.length} />
                </>
              )}
            </>
          )}
        </div>
      ) : (
        <div className="fb_body">
          {complaintView === "form" ? (
            <ComplaintForm
              visits={overview?.visits || []}
              initialTarget={deepAppointmentId ? `visit:${deepAppointmentId}` : ""}
              onCreated={onComplaintCreated}
              onCancel={() => setComplaintView("list")}
            />
          ) : complaintView === "done" ? (
            <div className="fb_card fb_done">
              <div className="fb_done_icon" aria-hidden="true">
                ✓
              </div>
              <h2 className="fb_survey_title">Complaint registered</h2>
              <p className="fb_muted">Your reference number is</p>
              <div className="fb_reference">{complaintResult?.referenceId}</div>
              <p className="fb_muted">
                Keep it for follow-up. The clinic will update you as the
                complaint progresses.
              </p>
              <button
                type="button"
                className="fb_btn_primary"
                onClick={() => {
                  setComplaintView("list");
                  setComplaintResult(null);
                }}
              >
                View my complaints
              </button>
            </div>
          ) : (
            <>
              <button
                type="button"
                className="fb_btn_primary fb_btn_block"
                onClick={() => setComplaintView("form")}
              >
                Raise a complaint
              </button>
              <h3 className="fb_section">My complaints</h3>
              <ComplaintsList
                complaints={complaints}
                loading={loadingComplaints}
                error={complaintsError}
              />
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default FeedbackScreen;
